import type { TranslateFn } from '@/lib/i18n'
/**
 * Single formatter for the import/export weight difference.
 *
 * WHY THIS EXISTS
 * The table cell used to print `weightDiffPct.toFixed(1)` or an em dash, which
 * collapses four genuinely different situations into two glyphs. An audit of all
 * 21 589 eligible rows showed both glyphs are ambiguous:
 *   • «—» covers "no weights at all" (4 285 rows) and "export weight absent while
 *     the import weight is 0" (251 rows) — different problems for an analyst;
 *   • «0.0%» covers exact equality (5 009 rows) AND a real, smaller difference
 *     rounded away (44 rows).
 * So the rules live here once, and the table, the drawer and the similar-case
 * cards all render from the same result.
 *
 * CONFIRMED FORMULA (do not re-derive; verified against the frozen pipeline)
 *   iw_valid = import_weight IS NOT NULL AND import_weight > 0
 *   ew_valid = export_weight IS NOT NULL AND export_weight > 0
 *   weight_diff_pct_clean = CASE WHEN iw_valid AND ew_valid
 *       THEN 100.0 * ABS(import_weight - export_weight)
 *            / GREATEST(import_weight, export_weight) END
 * Source: full_training/raw_pipeline/notebooks/05_feat_eng.ipynb, registered as
 * "100*|iw-ew|/max(iw,ew), только валидные веса". The denominator is the LARGER
 * of the two weights — NOT the import weight. The value is absolute (never
 * negative) and the anomaly candidate pairs are capped at 10 %, which the audit
 * confirmed: observed range 0 … 10, no negatives, no values over 100.
 *
 * PRECISION (what we may and may not claim)
 * The snapshot builder stores weights rounded to 3 decimals and the percentage
 * to 2. So a percentage of exactly 0 does NOT prove the weights are equal — the
 * audit found 6 rows where the stored percentage is 0 while the stored weights
 * differ. Equality is therefore asserted only when the stored weights themselves
 * match; otherwise we say the difference is below display precision and never
 * claim «вес совпадает».
 */

export type WeightStatus =
  | 'calculated'
  | 'exact_match'
  | 'below_display_precision'
  | 'missing_import'
  | 'missing_export'
  | 'missing_both'
  | 'zero_denominator'
  | 'invalid'

export interface WeightDifference {
  /** What the table cell prints: «5.3%», «0.0%», «< 0.1%» or «—». */
  displayValue: string
  /** Best available percentage, or null when it cannot be computed. */
  exactPercent: number | null
  status: WeightStatus
  /** Full reason / values — always specific, never a generic fallback. */
  tooltip: string
  /** «58.636 т» or null when that side has no usable weight. */
  importWeightText: string | null
  exportWeightText: string | null
  /** Ready-made «Детали кейса» line for the drawer. */
  detailText: string
}

export interface WeightInput {
  importWeight: number | null | undefined
  exportWeight: number | null | undefined
  /** Backend `weightDiffPct` — authoritative when present and > 0. */
  weightDiffPct: number | null | undefined
}

/** Below this the table would print «0.0%», which reads as exact equality. */
const DISPLAY_PRECISION = 0.05

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
/** Present, numeric and strictly positive — the pipeline's own validity test. */
const usable = (v: unknown): v is number => isNum(v) && v > 0

/** Tonnes with three decimals, matching the stored precision. */
export function formatWeight(v: number): string {
  return `${v.toFixed(3)} т`
}

/** «0.015%» — trims trailing zeros so small values stay readable. */
function formatPercent(p: number, maxDigits = 4): string {
  const s = p.toFixed(maxDigits).replace(/0+$/, '').replace(/\.$/, '')
  return `${s === '' || s === '-0' ? '0' : s}%`
}

export function formatWeightDifference(
  input: WeightInput,
  t?: TranslateFn,
): WeightDifference {
  /** Без t() отдаём русский: вызывающие без контекста перевода не ломаются. */
  const msg = (key: string, ru: string) => (t ? t(key) : ru)
  const iw = input.importWeight ?? null
  const ew = input.exportWeight ?? null
  const pct = input.weightDiffPct ?? null

  const importText = usable(iw) ? formatWeight(iw) : null
  const exportText = usable(ew) ? formatWeight(ew) : null
  const both = usable(iw) && usable(ew)

  // ── cannot compute ────────────────────────────────────────────────────────
  if (!both) {
    // Enumerate every defect found, so a row that is broken on both sides does
    // not hide behind the first reason we happen to hit.
    const problems: string[] = []
    let status: WeightStatus

    const badImport = iw !== null && !usable(iw)
    const badExport = ew !== null && !usable(ew)

    if ((iw !== null && !isNum(iw)) || (ew !== null && !isNum(ew)) || (isNum(iw) && iw < 0) || (isNum(ew) && ew < 0)) {
      status = 'invalid'
    } else if (iw === null && ew === null) {
      status = 'missing_both'
    } else if (iw === null) {
      status = 'missing_import'
    } else if (ew === null) {
      status = 'missing_export'
    } else {
      status = 'zero_denominator'
    }

    if (iw === null && ew === null) problems.push(msg('analytics.weight.noBoth', 'Нет веса обеих отправок'))
    else {
      if (iw === null) problems.push(msg('analytics.weight.noImport', 'Нет веса импортной отправки'))
      if (ew === null) problems.push(msg('analytics.weight.noExport', 'Нет веса экспортной отправки'))
    }
    if (badImport) {
      problems.push(isNum(iw) && iw === 0
        ? msg('analytics.weight.importZero', 'Невозможно рассчитать процент: импортный вес равен нулю')
        : msg('analytics.weight.importBad', 'Вес импортной отправки указан некорректно'))
    }
    if (badExport) {
      problems.push(isNum(ew) && ew === 0
        ? msg('analytics.weight.exportZero', 'Невозможно рассчитать процент: экспортный вес равен нулю')
        : msg('analytics.weight.exportBad', 'Вес экспортной отправки указан некорректно'))
    }
    // Whatever weight IS known stays visible — it is still useful on its own.
    const known = [importText && `Импорт: ${importText}`, exportText && `Экспорт: ${exportText}`].filter(Boolean)
    const tooltip = [...problems, ...known].join(' · ')

    const detail = known.length
      ? `${msg('analytics.weight.noData', 'Нет данных для расчёта разницы веса')} · ${known.join(' · ')}`
      : msg('analytics.weight.noData', 'Нет данных для расчёта разницы веса')

    return {
      displayValue: '—',
      exactPercent: null,
      status,
      tooltip,
      importWeightText: importText,
      exportWeightText: exportText,
      detailText: detail,
    }
  }

  // ── computable ────────────────────────────────────────────────────────────
  const pair = `Импорт: ${importText} · Экспорт: ${exportText}`

  // Equality is asserted from the weights, not from a percentage that may have
  // been rounded to zero.
  if (iw === ew) {
    return {
      displayValue: '0.0%',
      exactPercent: 0,
      status: 'exact_match',
      tooltip: `Вес импортной и экспортной отправок совпадает: ${importText} → ${exportText}`,
      importWeightText: importText,
      exportWeightText: exportText,
      detailText: `${iw.toFixed(3)} → ${ew.toFixed(3)} т · вес совпадает`,
    }
  }

  // The weights differ. Trust the backend percentage when it carries the value;
  // when it rounded to 0 the information is only left in the weights, so
  // recompute using the documented formula (max of the two as denominator).
  const recomputed = (100 * Math.abs(iw - ew)) / Math.max(iw, ew)
  const exact = isNum(pct) && pct > 0 ? pct : recomputed

  if (exact < DISPLAY_PRECISION) {
    return {
      displayValue: '< 0.1%',
      exactPercent: exact,
      status: 'below_display_precision',
      tooltip: `${pair} · Разница: ${formatPercent(exact)}`,
      importWeightText: importText,
      exportWeightText: exportText,
      detailText: `${iw.toFixed(3)} → ${ew.toFixed(3)} т · разница < 0.1%`,
    }
  }

  const shown = `${exact.toFixed(1)}%`
  return {
    displayValue: shown,
    exactPercent: exact,
    status: 'calculated',
    tooltip: pair,
    importWeightText: importText,
    exportWeightText: exportText,
    detailText: `${iw.toFixed(3)} → ${ew.toFixed(3)} т · разница ${shown}`,
  }
}

/** Column header tooltip. States only the formula the audit confirmed. */
export const WEIGHT_DELTA_HEADER_KEY = 'analytics.weight.deltaHeader'
export const WEIGHT_DELTA_TOOLTIP_KEY = 'analytics.weight.deltaTooltip'
